/**
 * Backend API Service
 *
 * Client service for communicating with Node.js backend
 * The backend runs the full Agents SDK workflow with:
 * - @openai/agents
 * - @openai/guardrails
 * - Vector Store search for real PDF content
 *
 * @module backendApiService
 * @created 2025-11-12
 * @author Newso Technologies Consultants Co., Ltd.
 */

const BACKEND_AI_URL = process.env.REACT_APP_BACKEND_AI_URL || 'https://localhost:3001/api';

// Log backend URL configuration (helpful for debugging deployment issues)

/**
 * Request payload for analyze endpoint
 */
export interface BackendAnalyzeRequest {
  query: string;
}

/**
 * Vision Image from PDF with page number
 */
export interface VisionImage {
  page: number;
  url: string;
}

/**
 * Response from backend analyze endpoint
 * Matches the output format from agent-builder workflow
 * Now includes support for images extracted from PDF
 */
export interface BackendAnalyzeResponse {
  output_text?: string;
  safe_text?: string;
  confidence?: number;
  has_images?: boolean;
  images?: VisionImage[];
  images_processed?: number;
  cost_estimate?: number;
  processing_time?: number;
  pii?: {
    failed: boolean;
    detected_counts?: string[];
  };
  moderation?: {
    failed: boolean;
    flagged_categories?: string[];
  };
  jailbreak?: {
    failed: boolean;
  };
}

/**
 * Backend API Service Class
 *
 * Handles all communication with the Node.js backend
 * which runs the OpenAI Agents SDK workflow
 */
export class BackendApiService {
  /**
   * Analyze query using backend API
   *
   * Sends user query to backend which executes:
   * 1. Security guardrails (@openai/guardrails)
   * 2. Vector Store search (reads actual PDF content)
   * 3. Text processing and pattern matching
   * 4. Formatted output generation
   *
   * @param query - User query text
   * @returns Workflow execution result with output_text
   */
  async analyze(query: string): Promise<BackendAnalyzeResponse> {
    try {
      /**
       * Send HTTP POST request to backend analyze endpoint
       * Backend will execute full Agents SDK workflow
       * Uses DOCX as primary source, PDF as fallback
       */
      const response = await fetch(`${BACKEND_AI_URL}/assistant/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`❌ [BackendAPI] HTTP ${response.status}:`, errorData.message);
        throw new Error(errorData.message || `Backend API error: ${response.status}`);
      }

      const result = await response.json();

      return result;
    } catch (error: any) {
      console.error('❌ [BackendAPI] Request failed:', error.message);
      console.error(
        '❌ [BackendAPI] Backend URL attempted:',
        `${BACKEND_AI_URL}/assistant/analyze`
      );
      console.error('❌ [BackendAPI] Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
      });

      /**
       * Provide helpful error message if backend is not reachable
       * Common causes: backend not running, wrong URL, CORS issues, network issues
       */
      if (
        error.message.includes('fetch') ||
        error.message.includes('NetworkError') ||
        error.message.includes('Failed to fetch')
      ) {
        const isProduction =
          !BACKEND_AI_URL.includes('localhost') && !BACKEND_AI_URL.includes('127.0.0.1');

        if (isProduction) {
          throw new Error(
            `ไม่สามารถเชื่อมต่อ Backend API ได้\n\n` +
              `Backend URL: ${BACKEND_AI_URL}\n\n` +
              `สาเหตุที่เป็นไปได้:\n` +
              `1. Backend server ไม่สามารถเข้าถึงได้\n` +
              `2. ปัญหา CORS (Cross-Origin Resource Sharing)\n` +
              `3. ปัญหา Network หรือ Firewall\n` +
              `4. Environment variable REACT_APP_BACKEND_AI_URL ไม่ได้ถูกตั้งค่า\n\n` +
              `กรุณาติดต่อผู้ดูแลระบบ`
          );
        } else {
          throw new Error(
            `ไม่สามารถเชื่อมต่อ Backend API ได้\n\n` +
              `Backend URL: ${BACKEND_AI_URL}\n\n` +
              `กรุณาตรวจสอบว่า:\n` +
              `1. Backend server กำลังรันอยู่ (port 3001)\n` +
              `2. รัน: cd FromasCloud-Backend && npm run dev\n` +
              `3. ตรวจสอบว่า REACT_APP_BACKEND_AI_URL ถูกตั้งค่าถูกต้อง`
          );
        }
      }

      throw error;
    }
  }

  /**
   * Check if backend server is available
   * @returns true if backend responds to health check
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${BACKEND_AI_URL}/health`);
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get configured backend URL
   * @returns Backend API base URL
   */
  getBackendUrl(): string {
    return BACKEND_AI_URL;
  }
}

/**
 * Singleton instance of BackendApiService
 * Use this throughout the application
 */
export const backendApiService = new BackendApiService();
